import { useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from 'context/AuthContext';
import { buscarIniciativasEtapa3 } from 'services/iniciativa.service';
import { IniciativaFormatada } from 'types/iniciativa';
import naveEspacial from 'assets/img/2026/ELBB/nave_espacial_hero.png';
import { HeaderElbb } from '../HomeElbb/components/Header';
import { HeroElbb } from '../HomeElbb/components/HeroElbb';
import { IniciativaCardElbb } from '../HomeElbb/components/IniciativaCardElbb';
import Spinner from 'components/ui/Spinner';
import './style.scss';

const resolveIndicadosFromParticipantes = (
  ini: IniciativaFormatada
): Array<{ posicao: number; nome: string }> =>
  (ini.participantes ?? []).map((p, idx) => ({
    posicao: idx + 1,
    nome: p.displayName || `${p.nome} (${p.matricula})`,
  }));

export function IndicadosElbb() {
  const { user } = useAuth();
  const [iniciativas, setIniciativas] = useState<IniciativaFormatada[]>([]);
  const [termoBusca, setTermoBusca] = useState('');
  const [debouncedTermo, setDebouncedTermo] = useState('');
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState<string | null>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setDebouncedTermo(termoBusca.trim());
    }, 350);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [termoBusca]);

  useEffect(() => {
    let cancelled = false;
    if (!user?._id) return;

    async function carregar() {
      try {
        setLoading(true);
        setErro(null);
        const items = await buscarIniciativasEtapa3(user!._id);
        if (cancelled) return;

        setIniciativas(items);
      } catch (error) {
        console.error('[IndicadosElbb] erro ao carregar', error);
        if (cancelled) return;
        setIniciativas([]);
        setErro('Não foi possível carregar as iniciativas. Tente novamente.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    carregar();
    return () => {
      cancelled = true;
    };
  }, [user?._id]);

  const iniciativasFiltradas = useMemo(() => {
    const termo = debouncedTermo.toLowerCase();
    if (termo.length === 0) return iniciativas;
    return iniciativas.filter((i) => i.titulo.toLowerCase().includes(termo));
  }, [iniciativas, debouncedTermo]);

  const topPorIniciativa = useMemo(() => {
    const m = new Map<string, Array<{ posicao: number; nome: string }>>();
    iniciativas.forEach((ini) => m.set(ini.id, resolveIndicadosFromParticipantes(ini)));
    return m;
  }, [iniciativas]);

  const handleBuscar = () => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    setDebouncedTermo(termoBusca.trim());
  };

  if (!user) return null;

  const getUserData = () => ({
    nome: user.name || 'Usuário',
    primeiraLetra: user.name?.charAt(0).toUpperCase() || 'U',
    foto: user.image?.small?.url || '',
  });

  return (
    <div className="indicados-elbb-wrapper">
      <HeaderElbb currentPage="indicados" />

      <HeroElbb
        user={getUserData()}
        badges={[]}
        isParticipanteConfirmado={false}
        ilustracao={naveEspacial}
      />

      <main className="indicados-elbb-main">
        <div className="indicados-elbb-main__container">
          <div className="indicados-elbb-main__header">
            <h2
              className="indicados-elbb-main__count"
              tabIndex={0}
              aria-live="polite"
            >
              {iniciativasFiltradas.length} iniciativas encontradas
            </h2>
            <div className="indicados-elbb-main__search">
              <input
                type="search"
                className="indicados-elbb-main__search-input"
                placeholder="Buscar por título"
                value={termoBusca}
                onChange={(e) => setTermoBusca(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleBuscar();
                }}
                aria-label="Buscar iniciativas por título"
              />
              <button
                type="button"
                className="indicados-elbb-main__search-btn"
                onClick={handleBuscar}
              >
                Buscar
              </button>
            </div>
          </div>

          {loading ? (
            <div className="flex justify-center items-center py-8">
              <Spinner />
            </div>
          ) : erro ? (
            <div className="indicados-elbb-main__empty" role="alert">
              <p className="indicados-elbb-main__empty-title" tabIndex={0}>{erro}</p>
            </div>
          ) : iniciativasFiltradas.length === 0 ? (
            <div
              className="indicados-elbb-main__empty"
              role="status"
              aria-live="polite"
            >
              <p className="indicados-elbb-main__empty-title" tabIndex={0}>
                Nenhuma iniciativa encontrada.
              </p>
            </div>
          ) : (
            <div className="indicados-elbb-main__grid">
              {iniciativasFiltradas.map((ini) => (
                <IniciativaCardElbb
                  key={ini.id}
                  {...ini}
                  topIndicados={topPorIniciativa.get(ini.id) ?? []}
                />
              ))}
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
